import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, CheckCheck, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NotificationBell — polls /api/notifications every 25s, shows unread count badge.
 * Dropdown lists recent items with click-through to associated link.
 */
export const NotificationBell = ({ className = '' }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef(null);
  const ref = useRef(null);

  const fetchNotifs = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (!token) return;
    fetchNotifs();
    intervalRef.current = setInterval(fetchNotifs, 25000);
    return () => clearInterval(intervalRef.current);
  }, [token]);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const markOneRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchNotifs();
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchNotifs();
    } catch (e) { /* silent */ }
  };

  const onClickItem = (n) => {
    if (!n.read) markOneRead(n.id);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-full bg-white dark:bg-cn-ink-800 border border-cn-border hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700 flex items-center justify-center transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4 text-cn-text" strokeWidth={2} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-cn-terracotta-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-unread-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-[360px] bg-white dark:bg-cn-ink-800 rounded-2xl shadow-cn-floating border border-cn-border overflow-hidden z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-cn-border">
            <div className="font-display font-semibold">Notifications</div>
            {items.length > 0 && unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-cn-primary hover:underline inline-flex items-center gap-1" data-testid="notification-mark-all-read">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-cn-text-muted">
                <Sparkles className="w-6 h-6 mx-auto mb-2 text-cn-secondary" />
                You&apos;re all caught up.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => onClickItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-cn-border/60 hover:bg-cn-bone-100 dark:hover:bg-white/5 transition-colors flex items-start gap-3 ${
                    !n.read ? 'bg-cn-sage-50/40 dark:bg-cn-primary/15' : ''
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!n.read ? 'bg-cn-primary' : 'bg-transparent'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-cn-text">{n.title}</div>
                    {n.body && <div className="text-xs text-cn-text-muted mt-0.5">{n.body}</div>}
                    <div className="text-[10px] text-cn-text-muted/70 mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {n.read && <Check className="w-3 h-3 text-cn-text-muted/50 mt-1" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
